import Vue from "vue";
import VueRouter from "vue-router";

// Routes
import { canNavigate } from "@/libs/acl/routeProtection";
import {
  isUserLoggedIn,
  getUserData,
  getHomeRouteForLoggedInUser,
  isPublicRoute,
} from "@/auth/utils";
import dashboard from "./routes/dashboard";
import Home from "@/views/dashboard/analytics/Analytics.vue";
import store from "@/store";
import { matchRouteToModule, getRouteType, normalizeModuleName } from "@/libs/acl/routePermission";
Vue.use(VueRouter);
const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },

  routes: dashboard,
});
router.beforeEach(async (to, _, next) => {
  const isLoggedIn = isUserLoggedIn();
  
  // Public routes (no login required)
  if (isPublicRoute(to)) {
    return next();
  }

  // Allow access to authentication routes (login, register, etc.) without ACL check
  const isAuthRoute = to.name && (to.name.startsWith('auth-') || to.name === 'error-404' || to.name === 'misc-not-authorized');
  
  // Routes that don't require rights check
  // Profile and account settings should be accessible to all logged-in users
  const freeArray = [
    "dashboard-analytics",
    "misc-not-authorized",
    "pages-profile",
    "pages-account-setting"
  ];
  
  // Allow free routes
  if (freeArray.includes(to.name)) {
    return next();
  }
  
  // Also allow profile and account-setting routes by path (in case route name differs)
  if (to.path === '/pages/profile' || to.path === '/pages/account-setting') {
    return next();
  }
  
  // Allow auth routes
  if (isAuthRoute) {
    // Redirect if logged in and trying to access auth pages
    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
      const userData = getUserData();
      return next(getHomeRouteForLoggedInUser(userData ? userData.role : null));
    }
    return next();
  }
  
  // If not logged in, redirect to login
  if (!isLoggedIn) {
    return next({ name: "auth-login" });
  }
  
  // Allow all logged-in users to access their profile and account settings
  // These routes should be accessible regardless of permissions
  if (to.path === '/pages/profile' || to.path === '/pages/account-setting' || 
      to.name === 'pages-profile' || to.name === 'pages-account-setting') {
    return next();
  }
  
  // Get rights data from localStorage
  // rightsData structure: { "/route/path": { route: "/route/path", ...menuItem } }
  let rightsData = {};
  try {
    const rightsDataStr = localStorage.getItem("rightsData");
    const userData = JSON.parse(localStorage.getItem("userData"));
    if(userData.role == 'admin'){
      return next();
    }
    if (rightsDataStr) {
      rightsData = JSON.parse(rightsDataStr);
    }
  } catch (error) {
    console.error("Error parsing rightsData:", error);
    // If rightsData is corrupted, allow navigation to dashboard
    return next({ name: "dashboard-analytics" });
  }
  
  // Check if user has rights for this route
  // Check by route path (primary check since rightsData keys are route paths)
  const hasRouteAccessByPath = rightsData[to.path] !== undefined;
  
  // Check by route name (fallback)
  const hasRouteAccessByName = to.name && rightsData[to.name] !== undefined;
  // Check if any rightsData entry has a route property matching current path/name
  let hasRouteAccessByProperty = false;
  if (Object.keys(rightsData).length > 0) {
    hasRouteAccessByProperty = Object.values(rightsData).some((item) => {
      if (item && typeof item === 'object') {
        return item.route === to.path || item.route === to.name || item.route === to.fullPath;
      }
      return false;
    });
  }
  
  // Check for add/edit route variations by path
  let hasAddAccess = false;
  let hasEditAccess = false;
  
  if (to.path) {
    // Check for add route path (e.g., "/crm/lead" -> "/crm/addlead")
    const pathParts = to.path.split("/").filter(p => p);
    if (pathParts.length > 0) {
      const lastPart = pathParts[pathParts.length - 1];
      const addPath = "/" + pathParts.slice(0, -1).join("/") + "/add" + lastPart;
      const editPath = "/" + pathParts.slice(0, -1).join("/") + "/edit" + lastPart;
      hasAddAccess = rightsData[addPath] !== undefined;
      hasEditAccess = rightsData[editPath] !== undefined;
    }
  }
  
  // Check for add/edit route variations by name
  if (to.name) {
    const routeParts = to.name.split("-");
    if (routeParts.length > 0) {
      const addRouteName = routeParts.slice(0, -1).join("-") + "-add" + routeParts[routeParts.length - 1];
      const editRouteName = routeParts.slice(0, -1).join("-") + "-edit" + routeParts[routeParts.length - 1];
      hasAddAccess = hasAddAccess || rightsData[addRouteName] !== undefined;
      hasEditAccess = hasEditAccess || rightsData[editRouteName] !== undefined;
    }
  }
  // If user has access (direct, add, or edit), allow navigation
  if (hasRouteAccessByPath || hasRouteAccessByName || hasRouteAccessByProperty || hasAddAccess || hasEditAccess) {
    return next();
  }
  
  // Check with canNavigate as fallback
  // if (canNavigate(to)) {
  //   return next();
  // }

  // Additional additive permission check using UserRights + module routes
  try {
    // Check if this is a dashboard route (e.g., /hr, /crm, /accounting)
    const isDashboardRoute = to.name && to.name.startsWith('dashboard-');
    const isSectionRoute = ['/hr', '/crm', '/accounting', '/master', '/reports'].includes(to.path);
    
    if (isDashboardRoute || isSectionRoute) {
      // For dashboard/section routes, check if user has ANY module permissions
      const userRights = JSON.parse(localStorage.getItem("UserRights") || "[]");
      const hasAnyPermission = userRights.some(right => 
        right.add === 1 || right.edit === 1 || right.view === 1 || right.delete === 1
      );
      
      // For section routes, check if user has any module/widget/approval/reminder permission in that section
      if (isSectionRoute) {
        const sectionPath = to.path; // e.g., "/hr", "/crm", "/master", "/reports"
        
        // Define section module patterns (module names that typically belong to each section)
        const sectionPatterns = {
          '/hr': ['hr', 'employee', 'daily working', 'leave', 'attendance', 'request', 'courier', 'holiday', 'designation', 'department'],
          '/crm': ['crm', 'lead', 'customer', 'site visit', 'task', 'followup', 'company associate partner', 'franchise', 'opportunity', 'quotation'],
          '/master': ['master', 'company', 'branch', 'user', 'module', 'language', 'country', 'state', 'city', 'vehicle', 'product'],
          '/reports': ['report', 'lead', 'customer', 'site visit', 'task', 'daily working', 'leave', 'attendance'],
          '/accounting': ['accounting', 'account', 'payment', 'receipt', 'invoice', 'voucher', 'ledger', 'bank']
        };
        
        const patterns = sectionPatterns[sectionPath] || [];
        const sectionPathLower = sectionPath.toLowerCase();
        
        // Check module permissions (UserRights) - check if any module name matches section patterns
        const hasModulePermission = userRights.some(right => {
          if (!right || !right.modulename) return false;
          const modulename = (right.modulename || '').toLowerCase();
          const hasPermission = right.add === 1 || right.edit === 1 || right.view === 1 || right.delete === 1;
          
          if (!hasPermission) return false;
          
          // Check if module name matches section patterns
          const matchesPattern = patterns.some(pattern => 
            modulename.includes(pattern) || pattern.includes(modulename)
          );
          
          // Also check if any route in rightsData starts with section path
          if (rightsData && Object.keys(rightsData).length > 0) {
            const hasRouteInSection = Object.keys(rightsData).some(route => {
              const routeLower = route.toLowerCase();
              return routeLower.startsWith(sectionPathLower) || routeLower.includes(sectionPathLower.replace('/', ''));
            });
            if (hasRouteInSection) return true;
          }
          
          return matchesPattern;
        });
        
        if (hasModulePermission) {
          return next();
        }
        
        // Check widget permissions for this section
        const userWidgets = store.state.app.user_widgets || [];
        if (userWidgets && userWidgets.length > 0) {
          const hasWidgetPermission = userWidgets.some(widget => {
            if (!widget || !widget.name) return false;
            const widgetView = widget.view === true || widget.view === 1;
            if (!widgetView) return false;
            
            const widgetName = (widget.name || '').toLowerCase();
            return patterns.some(pattern => 
              widgetName.includes(pattern) || pattern.includes(widgetName)
            );
          });
          
          if (hasWidgetPermission) {
            return next();
          }
        }
        
        // Check approval permissions for this section
        const userApprovals = store.state.app.user_approval || [];
        if (userApprovals && userApprovals.length > 0) {
          const hasApprovalPermission = userApprovals.some(approval => {
            if (!approval || !approval.module || !approval.module.name) return false;
            const moduleName = (approval.module.name || '').toLowerCase();
            return patterns.some(pattern => 
              moduleName.includes(pattern) || pattern.includes(moduleName)
            );
          });
          
          if (hasApprovalPermission) {
            return next();
          }
        }
        
        // Check reminder permissions for this section
        try {
          const userReminders = JSON.parse(localStorage.getItem('UserReminders') || '[]');
          if (userReminders && userReminders.length > 0) {
            const hasReminderPermission = userReminders.some(reminder => {
              if (!reminder || !reminder.name) return false;
              const reminderName = (reminder.name || '').toLowerCase();
              return patterns.some(pattern => 
                reminderName.includes(pattern) || pattern.includes(reminderName)
              );
            });
            
            if (hasReminderPermission) {
              return next();
            }
          }
        } catch (error) {
          // Ignore parsing errors
        }
      }
      
      // For section routes, only allow if user has specific section permissions
      // Don't allow access just because user has any permission - they must have permission for THIS section
      // This check is already done above in the section-specific permission checks
      // If we reach here, user doesn't have permission for this section, so block access
    } else {
      // For specific module routes, check exact permission
      const hasPermission = await store.dispatch("app/checkRouteAccess", { routePath: to.path, routeName: to.name });
      if (hasPermission) {
        return next();
      }
      
      // Fallback: Try to match route by path pattern and check UserRights directly
      // This handles cases where module routes might not be configured in the database
      const userRights = JSON.parse(localStorage.getItem("UserRights") || "[]");
      const routePath = to.path.toLowerCase();
      const routeName = (to.name || '').toLowerCase();
      
      // Extract potential module name from route path (e.g., /hr/courier -> courier)
      // Or from route name (e.g., hr-courier -> courier)
      let potentialModuleNames = [];
      
      // Try to extract from path: /hr/courier -> courier, /crm/lead -> lead
      // For routes like /crm/customer/addcustomer, extract "customer" not "addcustomer"
      // For report routes like /reports/hr/registerreport, extract "registerreport" or "register report"
      const pathParts = routePath.split('/').filter(p => p);
      if (pathParts.length >= 2) {
        // If last part is "add", "edit", "view" followed by module name, use second-to-last
        const lastPart = pathParts[pathParts.length - 1].toLowerCase();
        if (lastPart.startsWith('add') || lastPart.startsWith('edit') || lastPart.startsWith('view')) {
          // Remove "add"/"edit"/"view" prefix and use that, or use second-to-last part
          const moduleName = lastPart.replace(/^(add|edit|view)/, '');
          if (moduleName && moduleName.length > 0) {
            potentialModuleNames.push(moduleName);
          }
          // Also try second-to-last part (e.g., "customer" from "/crm/customer/addcustomer")
          if (pathParts.length >= 3) {
            potentialModuleNames.push(pathParts[pathParts.length - 2]);
          }
        } else {
          // Normal case: use last part
          potentialModuleNames.push(pathParts[pathParts.length - 1]);
          
          // For report routes, also try to extract module name from report name
          // e.g., "registerreport" -> "register report" or "register"
          if (lastPart.includes('report')) {
            const withoutReport = lastPart.replace(/report$/, '').trim();
            if (withoutReport) {
              potentialModuleNames.push(withoutReport);
              // Also try with space: "registerreport" -> "register report"
              if (withoutReport.length > 0) {
                potentialModuleNames.push(withoutReport + ' report');
              }
            }
          }
        }
      }
      
      // Try to extract from route name: hr-courier -> courier, crm-lead -> lead, add-customer -> customer
      // For report routes: reports-hr-registerreport -> registerreport or register
      if (routeName) {
        const nameParts = routeName.split('-');
        if (nameParts.length >= 2) {
          // If first part is "add", "edit", "view", use second part
          if (nameParts[0] === 'add' || nameParts[0] === 'edit' || nameParts[0] === 'view') {
            potentialModuleNames.push(nameParts[1]);
          } else {
            // For report routes like "reports-hr-registerreport", use the last part
            const lastPart = nameParts[nameParts.length - 1];
            potentialModuleNames.push(lastPart);
            
            // If it's a report route, also extract the base module name
            if (lastPart.includes('report')) {
              const baseName = lastPart.replace(/report$/i, '').trim();
              if (baseName) {
                potentialModuleNames.push(baseName);
                // Also try with space: "registerreport" -> "register report"
                potentialModuleNames.push(baseName + ' report');
              }
            }
          }
        }
        // Also try full route name without prefix: hr-courier -> courier, but also try hr-courier
        if (nameParts.length > 1) {
          potentialModuleNames.push(nameParts.slice(1).join('-'));
        }
      }
      
      // Route/module synonyms to improve matching when backend names differ
      const synonyms = {
        courier: ['courier', 'request'],
        request: ['courier', 'request'],
        sitevisit: ['site visit', 'sitevisit', 'site-visit'],
        'site visit': ['site visit', 'sitevisit', 'site-visit'],
        'site-visit': ['site visit', 'sitevisit', 'site-visit'],
        addcustomer: ['customer'],
        editcustomer: ['customer'],
        customer: ['customer'],
        registerreport: ['register', 'register report'],
        'register report': ['register', 'register report'],
        register: ['register', 'register report'],
        courierreport: ['courier', 'request'],
        'courier report': ['courier', 'request'],
        leadreport: ['lead'],
        'lead report': ['lead'],
        taskreport: ['task'],
        'task report': ['task'],
        dailyworkingreport: ['daily working', 'daily working report'],
        'daily working report': ['daily working', 'daily working report'],
      };
      const allPotentialNames = [...new Set(potentialModuleNames)]; // Remove duplicates
      potentialModuleNames.forEach(name => {
        const syns = synonyms[name];
        if (syns && syns.length) {
          allPotentialNames.push(...syns);
        }
      });

      // Check if user has permissions for any of these potential module names
      for (const potentialModuleName of allPotentialNames) {
        if (!potentialModuleName) continue;
        const normalizedPotentialName = normalizeModuleName(potentialModuleName);
        
        // Try exact match (case-insensitive)
        let moduleRights = userRights.find(r => {
          const modName = (r.modulename || '').toLowerCase();
          return modName === potentialModuleName.toLowerCase();
        });
        
        // Try normalized match (handles spaces, hyphens, etc.)
        // e.g., "Site Visit" -> "sitevisit" matches "sitevisit"
        if (!moduleRights) {
          moduleRights = userRights.find(r => {
            const modName = normalizeModuleName(r.modulename || '');
            return modName === normalizedPotentialName;
          });
        }
        
        // Try with first letter capitalized (Courier, Lead, etc.)
        if (!moduleRights) {
          const capitalizedName = potentialModuleName.charAt(0).toUpperCase() + potentialModuleName.slice(1);
          moduleRights = userRights.find(r => {
            const modName = (r.modulename || '').toLowerCase();
            return modName === capitalizedName.toLowerCase();
          });
        }
        
        // Try partial match with normalized names (e.g., "sitevisit" matches "Site Visit")
        // But be more strict - only match if one is clearly contained in the other
        if (!moduleRights) {
          moduleRights = userRights.find(r => {
            const modName = normalizeModuleName(r.modulename || '');
            // Only match if the normalized names have significant overlap
            // This prevents false matches like "registerreport" matching "register" when user doesn't have "Register Report" permission
            const modNameLower = modName.toLowerCase();
            const potentialNameLower = normalizedPotentialName.toLowerCase();
            
            // Check if one contains the other, but require at least 3 characters match
            if (modNameLower.length >= 3 && potentialNameLower.length >= 3) {
              return (modNameLower.includes(potentialNameLower) && potentialNameLower.length >= 3) || 
                     (potentialNameLower.includes(modNameLower) && modNameLower.length >= 3);
            }
            return false;
          });
        }
        
        // Try partial match (e.g., "courier" matches "Courier" or "Request" if route contains it)
        // But be more strict to prevent false matches
        if (!moduleRights) {
          moduleRights = userRights.find(r => {
            const modName = (r.modulename || '').toLowerCase();
            const potentialNameLower = potentialModuleName.toLowerCase();
            
            // Only match if one clearly contains the other and both have meaningful length
            if (modName.length >= 3 && potentialNameLower.length >= 3) {
              return (modName.includes(potentialNameLower) && potentialNameLower.length >= 3) || 
                     (potentialNameLower.includes(modName) && modName.length >= 3);
            }
            return false;
          });
        }
        
        if (moduleRights) {
          const routeType = getRouteType(to.path, to.name);
          let hasAccess = false;
          
          switch (routeType) {
            case 'add':
              hasAccess = moduleRights.add === 1;
              break;
            case 'edit':
              hasAccess = moduleRights.edit === 1;
              break;
            case 'view':
              hasAccess = moduleRights.view === 1;
              break;
            case 'listing':
            default:
              hasAccess = moduleRights.add === 1 || moduleRights.edit === 1 || 
                         moduleRights.view === 1 || moduleRights.delete === 1;
              break;
          }
          
          if (hasAccess) {
            return next();
          }
        }
      }
      
      // Additional fallback: Check all user rights for any module that might match the route
      // This is a last resort to catch edge cases
      const routeLastPart = pathParts.length > 0 ? pathParts[pathParts.length - 1] : '';
      const routeNameLastPart = routeName ? routeName.split('-').pop() : '';
      const normalizedRouteLastPart = normalizeModuleName(routeLastPart);
      const normalizedRouteNameLastPart = normalizeModuleName(routeNameLastPart);
      
      for (const right of userRights) {
        if (!right.modulename) continue;
        const modName = (right.modulename || '').toLowerCase();
        const normalizedModName = normalizeModuleName(right.modulename || '');
        const hasAnyPermission = right.add === 1 || right.edit === 1 || right.view === 1 || right.delete === 1;
        
        if (!hasAnyPermission) continue;
        
        // Check if route path or name contains the module name (case-insensitive)
        // Also check if module name contains any part of the route
        // Use both original and normalized names for matching
        const matchesRoute = routePath.includes(modName) || 
                            routeName.includes(modName) ||
                            routePath.includes(normalizedModName) ||
                            routeName.includes(normalizedModName) ||
                            (routeLastPart && modName.includes(routeLastPart)) ||
                            (routeNameLastPart && modName.includes(routeNameLastPart)) ||
                            (routeLastPart && routeLastPart.includes(modName)) ||
                            (routeNameLastPart && routeNameLastPart.includes(modName)) ||
                            (normalizedRouteLastPart && normalizedModName.includes(normalizedRouteLastPart)) ||
                            (normalizedRouteNameLastPart && normalizedModName.includes(normalizedRouteNameLastPart)) ||
                            (normalizedRouteLastPart && normalizedRouteLastPart.includes(normalizedModName)) ||
                            (normalizedRouteNameLastPart && normalizedRouteNameLastPart.includes(normalizedModName));
        
        if (matchesRoute) {
          const routeType = getRouteType(to.path, to.name);
          let hasAccess = false;
          
          switch (routeType) {
            case 'add':
              hasAccess = right.add === 1;
              break;
            case 'edit':
              hasAccess = right.edit === 1;
              break;
            case 'view':
              hasAccess = right.view === 1;
              break;
            case 'listing':
            default:
              hasAccess = right.add === 1 || right.edit === 1 || right.view === 1 || right.delete === 1;
              break;
          }
          
          if (hasAccess) {
            return next();
          }
        }
      }
      
      // Removed overly permissive fallback - users must have explicit permission for the route
      // This ensures that users cannot access routes by pasting URLs if they don't have permissions
    }
  } catch (error) {
    console.error("Route permission check error:", error);
    // Fall through to existing unauthorized handling
  }
  
  // Check Widget permissions (additive check)
  try {
    const userWidgets = store.state.app.user_widgets || [];
    if (userWidgets && userWidgets.length > 0) {
      // Check if this route matches any widget the user has access to
      const routePathLower = to.path.toLowerCase();
      const routeNameLower = (to.name || '').toLowerCase();
      
      const hasWidgetAccess = userWidgets.some(widget => {
        if (!widget || !widget.name) return false;
        const widgetName = (widget.name || '').toLowerCase();
        const widgetView = widget.view === true || widget.view === 1;
        
        if (!widgetView) return false;
        
        // Normalize widget name (remove spaces)
        const normalizedWidgetName = widgetName.replace(/\s+/g, '');
        const normalizedRoutePath = routePathLower.replace(/\s+/g, '');
        const normalizedRouteName = routeNameLower.replace(/\s+/g, '');
        
        // Check if route path or name contains widget name
        // e.g., widget "Site Visit" matches route "/crm/sitevisit"
        return normalizedRoutePath.includes(normalizedWidgetName) ||
               normalizedRouteName.includes(normalizedWidgetName) ||
               normalizedWidgetName.includes(normalizedRoutePath.split('/').pop()) ||
               normalizedWidgetName.includes(normalizedRouteName.split('-').pop());
      });
      
      if (hasWidgetAccess) {
        return next();
      }
    }
  } catch (error) {
    console.error("Widget permission check error:", error);
  }
  
  // Check Approval permissions (additive check)
  try {
    const userApprovals = store.state.app.user_approval || [];
    if (userApprovals && userApprovals.length > 0) {
      const routePathLower = to.path.toLowerCase();
      const routeNameLower = (to.name || '').toLowerCase();
      
      const hasApprovalAccess = userApprovals.some(approval => {
        if (!approval || !approval.module || !approval.module.name) return false;
        const moduleName = (approval.module.name || '').toLowerCase().replace(/\s+/g, '');
        const normalizedRoutePath = routePathLower.replace(/\s+/g, '');
        const normalizedRouteName = routeNameLower.replace(/\s+/g, '');
        
        // Check if route matches the approval module
        return normalizedRoutePath.includes(moduleName) ||
               normalizedRouteName.includes(moduleName);
      });
      
      if (hasApprovalAccess) {
        return next();
      }
    }
  } catch (error) {
    console.error("Approval permission check error:", error);
  }
  
  // Check Reminder permissions (additive check)
  // Note: Reminders might be stored differently, adjust based on actual storage structure
  try {
    // Check if reminders are stored in localStorage or store
    const userReminders = JSON.parse(localStorage.getItem('UserReminders') || '[]');
    if (userReminders && userReminders.length > 0) {
      const routePathLower = to.path.toLowerCase();
      const routeNameLower = (to.name || '').toLowerCase();
      
      const hasReminderAccess = userReminders.some(reminder => {
        if (!reminder || !reminder.name) return false;
        const reminderName = (reminder.name || '').toLowerCase().replace(/\s+/g, '');
        const normalizedRoutePath = routePathLower.replace(/\s+/g, '');
        const normalizedRouteName = routeNameLower.replace(/\s+/g, '');
        
        // Check if route matches the reminder
        return normalizedRoutePath.includes(reminderName) ||
               normalizedRouteName.includes(reminderName);
      });
      
      if (hasReminderAccess) {
        return next();
      }
    }
  } catch (error) {
    console.error("Reminder permission check error:", error);
  }
  
  // No access - redirect to unauthorized page
  // Prevent infinite loop by checking if we're already going to unauthorized page
  if (to.name !== "misc-not-authorized") {
    return next({ name: "misc-not-authorized" });
  }
  
  // Fallback: redirect to dashboard if unauthorized page also fails
  return next({ name: "dashboard-analytics" });
});

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});
export default router;
